/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  EvidenceAdvisoryChange,
  EvidenceAdvisoryChange$inboundSchema,
} from "./evidenceadvisorychange.js";
import {
  EvidenceFieldChange,
  EvidenceFieldChange$inboundSchema,
} from "./evidencefieldchange.js";

/**
 * What moved between the latest decision's evidence snapshot and the current gather, restricted to the permission-relevant slice: OAuth scopes, authority mode, demanded credentials, and published advisories. A change here is a re-review trigger, never a verdict — an unchanged published interface says nothing about unchanged behavior.
 */
export type EvidenceDiff = {
  /**
   * Advisories in the current gather's most-recent sample that the snapshot's sample did not carry.
   */
  advisoriesAdded?: Array<EvidenceAdvisoryChange> | undefined;
  /**
   * Whether anything below is non-empty.
   */
  changed: boolean;
  /**
   * Scalar drifts: authority mode, dynamic client registration, published-advisory count.
   */
  fields?: Array<EvidenceFieldChange> | undefined;
  /**
   * OAuth scopes the server's published authority metadata gained since the decision.
   */
  scopesAdded?: Array<string> | undefined;
  /**
   * OAuth scopes the published authority metadata lost since the decision.
   */
  scopesRemoved?: Array<string> | undefined;
  /**
   * Credentials the server now demands that it did not at decision time.
   */
  secretsAdded?: Array<string> | undefined;
  /**
   * Credentials the server demanded at decision time and no longer does.
   */
  secretsRemoved?: Array<string> | undefined;
};

/** @internal */
export const EvidenceDiff$inboundSchema: z.ZodMiniType<EvidenceDiff, unknown> =
  z.pipe(
    z.object({
      advisories_added: z.optional(
        z.array(EvidenceAdvisoryChange$inboundSchema),
      ),
      changed: z.boolean(),
      fields: z.optional(z.array(EvidenceFieldChange$inboundSchema)),
      scopes_added: z.optional(z.array(z.string())),
      scopes_removed: z.optional(z.array(z.string())),
      secrets_added: z.optional(z.array(z.string())),
      secrets_removed: z.optional(z.array(z.string())),
    }),
    z.transform((v) => {
      return remap$(v, {
        "advisories_added": "advisoriesAdded",
        "scopes_added": "scopesAdded",
        "scopes_removed": "scopesRemoved",
        "secrets_added": "secretsAdded",
        "secrets_removed": "secretsRemoved",
      });
    }),
  );

export function evidenceDiffFromJSON(
  jsonString: string,
): SafeParseResult<EvidenceDiff, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => EvidenceDiff$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'EvidenceDiff' from JSON`,
  );
}
